<template>
	<div class="lang-switcher" v-tooltip.bottom="$t('header.langswitcher')">
		<button @click="toggleMenu" ref="buttonRef" class="lang-btn" :class="isMobile ? 'mobile' : 'desktop'">
			{{ currentLabel }}
			<IconArrowBottom />
		</button>
		<Menu ref="menuRef" :model="langItems" :popup="true" />
	</div>
</template>

<script setup lang="ts">
	import { ref, computed } from "vue"
	import { useI18n } from "vue-i18n"
	import Menu from "primevue/menu"
	import IconArrowBottom from "../../icons/IconArrowBottom.vue"
	import { refreshTokenIfNeeded } from "../../composables/useAuth"
	import { useUserStore } from "../../stores/user"
	import api from "../../composables/useApi"
	import { url } from "../../helper"
	const userStore = useUserStore()
	const { locale, availableLocales } = useI18n()
	const menuRef = ref()
	const buttonRef = ref()

	const props = withDefaults(
		defineProps<{
			isMobile?: boolean
		}>(),
		{
			isMobile: false,
		}
	)

	const languages = [
		{ code: "en", label: "English" },
		{ code: "el", label: "Ελληνικά" },
		// Add more languages as needed
	]

	const currentLabel = computed(() => {
		const currentLocale = languages.find((l) => l.code === locale.value)?.label || locale.value
		return props.isMobile ? currentLocale.substring(0, 2) : currentLocale
	})

	const langItems = languages.map((lang) => ({
		label: lang.label,
		command: () => {
			locale.value = lang.code
			switchLang(lang.code)
		},
		icon: locale.value === lang.code ? "pi pi-check" : undefined,
	}))

	async function switchLang(langCode: string) {
		if (userStore.state.isLoggedIn) {
			await api
				.patch(
					`${url}/jsonapi/user/user/${userStore.state.user.id}`,
					{
						data: {
							type: "user--user",
							id: userStore.state.user.id,
							attributes: {
								preferred_langcode: langCode,
							},
						},
					},
					{
						headers: { "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json" },
					}
				)
				.then(async (response) => {
					// console.log("Successfully updated preferred language:", response.data)
					localStorage.setItem("preferred_langcode", langCode)
				})
				.catch((error) => {
					console.error("Error updating preferred language:", error)
				})
		}

		localStorage.removeItem("jwt")
		await refreshTokenIfNeeded()
		const newurl = new URL(window.location.href)
		newurl.searchParams.set("lang", langCode)
		window.location.href = newurl.toString()
	}

	function toggleMenu(event: Event) {
		menuRef.value.toggle(event)
	}
</script>

<style scoped lang="scss">
	.lang-switcher {
		display: inline-block;
		position: relative;
	}
	.lang-btn {
		background: transparent;
		border: 1px solid #ccc;
		border-radius: 4px;
		padding: 0.5rem 1rem;
		font-size: 1rem;
		cursor: pointer;
		color: #fff;
		background: rgba(0, 0, 0, 0.3);
		transition: background 0.2s;
		display: flex;
		align-items: center;
		border-radius: 16px;

		&.mobile {
			padding: 0.2rem 0.8rem;
			font-size: 0.9rem;
		}

		svg {
			width: 1rem;
			height: 1rem;
		}
	}
	.lang-btn:hover {
		background: rgba(0, 0, 0, 0.5);
	}

	:global(.p-menu) {
		--p-menu-background: black;
		--p-menu-border-radius: 16px;
	}
</style>
